var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
export class EpsonVp21 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: `ESC/VP.net\x10\x03\x00\x00\x00\x00`,
                next: /ESC\/VP\.net.\x03\x00\x00\x20\x00|ERR/i, // RS-232 responds with ERR
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`MUTE?`, /MUTE=(.+)\x0d|ERR/i, tags);
                if (match[1] !== undefined)
                    return match[1].toUpperCase() === 'ON';
                return this.state.displayBlank.value;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`MUTE ${blank ? 'ON' : 'OFF'}`, /^$|^(ERR)\x0d/i, tags);
                if (match[1] === undefined)
                    return blank;
                return this.state.displayBlank.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.displayFreeze = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`FREEZE?`, /FREEZE=(.+)\x0d|ERR/i, tags);
                if (match[1] !== undefined)
                    return match[1].toUpperCase() === 'ON';
                return this.state.displayFreeze.value;
            }),
            set: (freeze, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`FREEZE ${freeze ? 'ON' : 'OFF'}`, /^$|^(ERR)\x0d/i, tags);
                if (match[1] === undefined)
                    return freeze;
                return this.state.displayFreeze.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PWR?`, /^PWR=(.+)\x0d|^(ERR)\x0d/i, tags);
                if (match[1] !== undefined)
                    return match[1] === '01' || match[1] === '02';
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // If the projector is powered off too soon after powering on, then powering off will
                // return ERR. So, we keep retrying for a while.
                //
                // Also, on one model we've seen in the field, it doesn't respond to the power on
                // command and then drops its connection after about 20 seconds. So, we keep retrying
                // even after timeouts, but try to reconnect before 20 seconds.
                const prevPower = this.state.power.value;
                this.state.busy.update(true);
                this.state.power.update(power);
                let gotOkResponse = false;
                this.framework.suspendPolling();
                const waitTimeout = this.timeouts.set(() => { }, 60000);
                let numTimeouts = 0;
                while (waitTimeout.isRunning) {
                    let timedOut = false;
                    try {
                        const match = yield this.socket.send(`PWR ${power ? 'ON' : 'OFF'}`, /^$|^(ERR)\x0d/i, tags);
                        if (match[1] === undefined) {
                            gotOkResponse = true;
                            break;
                        }
                    }
                    catch (error) {
                        if (error instanceof TransportError && error.code === 'sendTimeout') {
                            this.log.info(`timeout waiting for power command`);
                            timedOut = true;
                            if (++numTimeouts >= 2) {
                                this.timeouts.clearAll();
                                yield this.framework.reconnect({
                                    initialDelayMs: 500,
                                    attemptDelayMs: 5000,
                                    numAttempts: 3,
                                });
                                return power;
                            }
                        }
                        else {
                            this.log.error(error);
                            waitTimeout.clear();
                        }
                    }
                    // Wait before trying to send the next power command.
                    yield this.timeouts.delay(timedOut ? 1 : 3000);
                }
                if (!gotOkResponse) {
                    const match = yield this.socket.send(`PWR?`, /^PWR=(.+)\x0d|^(ERR)\x0d/i, tags);
                    if (match[1] !== undefined) {
                        power = match[1] === '01' || match[1] === '02';
                    }
                    else {
                        power = prevPower;
                    }
                }
                this.framework.suspendPolling(2000);
                if (gotOkResponse) {
                    this.busyTimeout.set(() => this.state.busy.update(false), power ? 30000 : 10000);
                    this.framework.suspend(power ? 20000 : 10000);
                }
                else {
                    this.state.busy.update(false);
                }
                return power;
            }),
            asyncHandlers: {
                regExp: /^IMEVENT=0001 (\d+) .+/i,
                callback: (match) => {
                    if (match[1] === '01') {
                        this.state.power.update(false); // power off
                    }
                    else if (match[1] === '02' || match[1] === '03') {
                        this.state.power.update(true); // warm up
                    }
                },
            },
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`SOURCE?`, /^SOURCE=(.+)\x0d|^ERR\x0d/i, tags);
                if (match[1] !== undefined)
                    return match[1];
                return this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                const match = yield this.socket.send(`SOURCE ${code}`, /^$|^(ERR)\x0d/i, tags, {
                    sendTimeoutMs: 6000,
                });
                this.busyTimeout.set(() => this.state.busy.update(false), 3000);
                if (match[1] === undefined)
                    return code;
                return this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOL?`, /^VOL=(.+)\x0d|^ERR\x0d/i, tags);
                if (match[1] !== undefined)
                    return parseInt(match[1], 10);
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOL ${volume}`, /^$|^(ERR)\x0d/i, tags);
                if (match[1] === undefined)
                    return volume;
                return this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 255, min: 0, round: 1 },
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                controls: ['power', 'videoInput', 'volume', 'displayBlank', 'displayFreeze'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
}
// static properties
EpsonVp21.driverType = 'socketDriver';
EpsonVp21.key = '-KhyiWQkKIUKd8lDV9Ij';
EpsonVp21.baseSocketOptions = {
    commandExtractor: {
        terminators: [':'],
        allowEmptyCommands: true,
    },
    sendTimeoutMs: 3000,
};
EpsonVp21.socketOptions = [
    Object.assign(Object.assign({}, EpsonVp21.baseSocketOptions), { port: 3629 }),
    Object.assign(Object.assign({}, EpsonVp21.baseSocketOptions), { port: 4999 }),
];
